"use client"

import {useEffect, useMemo, useState} from "react";
import Link from "next/link";
import {usePathname} from "next/navigation";
import {chatPath, chatsPath} from "@/app/paths";
import {sortWorkspaceChats, workspaceChatTitle} from "@/app/components/chat/chatHistory";
import {SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem} from "@/components/ui/sidebar";
import {useBoundStore} from "@/hooks/queries/stores/use-bound-store";
import {WorkspaceChat} from "@/pb/api/devplan/types/workspace_chats";

const visibleFavoriteLimit = 4;

export function ChatSidebarFavorites({companyId}: {companyId: number}) {
  const pathname = usePathname();
  const {workspaceChats, workspaceChatById, fetchWorkspaceChats} = useBoundStore();
  const [loading, setLoading] = useState(workspaceChats[companyId] === undefined);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    void fetchWorkspaceChats(companyId)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [companyId, fetchWorkspaceChats]);

  const favorites = useMemo(() => {
    const chats = (workspaceChats[companyId] ?? [])
      .map(id => workspaceChatById[id])
      .filter((chat): chat is WorkspaceChat => !!chat && Boolean(chat.details?.favorite));
    return sortWorkspaceChats(chats);
  }, [companyId, workspaceChatById, workspaceChats]);
  const visible = favorites.slice(0, visibleFavoriteLimit);

  if (!loading && !error && visible.length === 0) return null;

  return (
    <SidebarMenuSub className="group-data-[collapsible=icon]:hidden" aria-label="Favorite Weaver conversations">
      {visible.map(chat => {
        const href = chatPath(companyId, chat.id);
        const title = workspaceChatTitle(chat);
        return (
          <SidebarMenuSubItem key={chat.id}>
            <SidebarMenuSubButton asChild isActive={pathname === href} className="text-[13px] font-medium">
              <Link href={href} title={title}>
                <span className="truncate">{title}</span>
              </Link>
            </SidebarMenuSubButton>
          </SidebarMenuSubItem>
        );
      })}
      {loading && visible.length === 0 && (
        <SidebarMenuSubItem>
          <span className="block px-2 py-1 text-xs text-muted-foreground">Loading favorites…</span>
        </SidebarMenuSubItem>
      )}
      {error && (
        <SidebarMenuSubItem>
          <span className="block px-2 py-1 text-xs text-destructive">Favorites unavailable</span>
        </SidebarMenuSubItem>
      )}
      {favorites.length > visibleFavoriteLimit && (
        <SidebarMenuSubItem>
          <SidebarMenuSubButton asChild size="sm" className="text-muted-foreground">
            <Link href={chatsPath(companyId)}><span className="truncate">Show all</span></Link>
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      )}
    </SidebarMenuSub>
  );
}
